import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, Calendar, Bookmark, ThumbsUp, Heart, ExternalLink, Pencil, ShieldOff, AlertTriangle, BookmarkCheck, PowerOff, MessageSquare } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { ManhwaRecap } from '../types';
import { ApiService } from '../services/api';
import toast from 'react-hot-toast';
import { StarRating } from './StarRating';
import { useWatchlist } from '../context/WatchlistContext';
import { useSharedFavorites } from '../FavoritesContext';
import { formatDuration, formatDateShort, getYouTubeUrl } from '../utils/formatters';
import SeriesSuggestionModal from './SeriesSuggestionModal';

interface RecapCardProps {
  recap: ManhwaRecap;
  onClick?: (recap: ManhwaRecap) => void;
}

export const RecapCard: React.FC<RecapCardProps> = React.memo(({ recap, onClick }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { watchlist, addToWatchlist, removeFromWatchlist } = useWatchlist();
  const { isFavorite, toggleFavorite } = useSharedFavorites();

  const [currentRecap, setCurrentRecap] = useState(recap);
  const [currentRating, setCurrentRating] = useState(recap.avg_rating);
  const [ratingCount, setRatingCount] = useState(recap.rating_count);
  const [userRating, setUserRating] = useState(recap.user_rating);
  const [isHovering, setIsHovering] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);

  const isBookmarked = watchlist.includes(recap.id);
  const isFavorited = isFavorite(recap.id);
  const isPrivateOrError = currentRecap.status === 'private' || currentRecap.status === 'error';

  // Debug: Log preview URL if available
  if (currentRecap.preview_url) {
    console.log(`Recap ${currentRecap.id} has preview URL:`, currentRecap.preview_url);
  }

  const handleRate = async (rating: number) => {
    if (!isAuthenticated) {
      toast.error('You must be logged in to rate.');
      return;
    }
    try {
      const response = await ApiService.rateRecap(recap.id.toString(), rating);
      setCurrentRating(parseFloat(response.avg_rating));
      setRatingCount(response.rating_count);
      setUserRating(rating);
      toast.success('Rating submitted!');
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Failed to submit rating.');
    }
  };

  const handleBookmark = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error('Please log in to add items to your watchlist.');
      return;
    }
    try {
      if (isBookmarked) {
        await removeFromWatchlist(recap.id);
        toast.success('Removed from watchlist');
      } else {
        await addToWatchlist(recap.id);
        toast.success('Added to watchlist');
      }
    } catch (error) {
      console.error('Failed to update watchlist', error);
      toast.error('Failed to update watchlist');
    }
  };

  const handleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error('Please log in to manage favorites.');
      return;
    }
    await toggleFavorite(recap.id);
  };

  const handleQuickUpdate = async (e: React.MouseEvent, status: 'private' | 'error') => {
    e.stopPropagation();
    try {
      const updatedRecap = await ApiService.updateRecap(currentRecap.id.toString(), { status });
      setCurrentRecap(updatedRecap);
      toast.success(`Recap marked as ${status}.`);
    } catch (error) {
      console.error('Failed to update recap status', error);
      toast.error('Failed to update status.');
    }
  };

  const handleDeactivateChannel = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!recap.channel_id) {
      toast.error('No channel ID available');
      return;
    }

    if (!confirm(`Are you sure you want to deactivate the channel "${currentRecap.channel_name}"? This will hide all recaps from this channel.`)) {
      return;
    }

    try {
      await ApiService.deactivateChannel(recap.channel_id);
      toast.success(`Channel "${currentRecap.channel_name}" has been deactivated successfully.`);
    } catch (error) {
      console.error('Error deactivating channel:', error);
      toast.error('Failed to deactivate channel. Please try again.');
    }
  };

  const handleSuggestSeriesName = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error('Please log in to suggest series names.');
      return;
    }
    setShowSuggestionModal(true);
  };

  const handleCardClick = () => {
    if (!isPrivateOrError && onClick) {
      onClick(recap);
    }
  };

  return (
    <div
      className={`relative bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-lg transition-all duration-300 group border-2 border-transparent ${isPrivateOrError ? 'opacity-50 grayscale' : 'cursor-pointer transform hover:-translate-y-1 hover:scale-105 hover:border-purple-500'}`}
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="relative pt-[56.25%] bg-black">
        {Boolean(user?.is_admin) && (
          <div className="absolute top-2 left-2 z-20 flex items-center space-x-1 bg-gray-900/60 p-1 rounded-full">
            <button onClick={(e) => handleQuickUpdate(e, 'private')} className="p-1.5 text-white hover:text-yellow-400 transition-colors" title="Mark as Private">
              <ShieldOff size={16} />
            </button>
            <button onClick={(e) => handleQuickUpdate(e, 'error')} className="p-1.5 text-white hover:text-red-400 transition-colors" title="Mark as Error">
              <AlertTriangle size={16} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); navigate(`/recaps/${recap.id}/edit`); }} className="p-1.5 text-white hover:text-purple-400 transition-colors" title="Edit Recap">
              <Pencil size={16} />
            </button>
          </div>
        )}
        {isHovering && currentRecap.preview_url ? (
          <video
            src={currentRecap.preview_url}
            className="absolute top-0 left-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            onError={(e) => {
              console.log('Video preview failed to load:', currentRecap.preview_url);
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <img
            src={currentRecap.thumbnail_url}
            alt={currentRecap.title}
            className="absolute top-0 left-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            fetchPriority="low"
          />
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 text-xs text-white font-medium">
              <div className="flex items-center space-x-1 bg-black/40 px-2 py-1 rounded"><Eye className="h-4 w-4" /><span>{currentRecap.views.toLocaleString()}</span></div>
              <div className="flex items-center space-x-1 bg-black/40 px-2 py-1 rounded"><ThumbsUp className="h-4 w-4" /><span>{currentRecap.like_count?.toLocaleString() || '0'}</span></div>
            </div>
            <span className="text-xs font-medium text-white bg-black/50 px-2 py-1 rounded">
              {formatDuration(currentRecap.duration)}
            </span>
          </div>
        </div>
        {isHovering && (
          <div className="absolute top-2 right-2 flex items-center space-x-2">
            <a href={getYouTubeUrl(recap.video_id)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-75 transition-opacity" title="Watch on YouTube">
              <ExternalLink className="h-5 w-5 text-white" />
            </a>
            <button onClick={handleBookmark} className="p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-75 transition-opacity" title={isBookmarked ? 'Remove from Watchlist' : 'Add to Watchlist'}>
              {isBookmarked ? <BookmarkCheck className="h-5 w-5 text-purple-400" /> : <Bookmark className="h-5 w-5 text-white" />}
            </button>
            <button onClick={handleFavorite} className="p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-75 transition-opacity" title={isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}>
              {isFavorited ? <Heart className="h-5 w-5 text-red-500" fill="currentColor" /> : <Heart className="h-5 w-5 text-white" />}
            </button>
            <button onClick={handleSuggestSeriesName} className="p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-75 transition-opacity" title="Suggest Series Name">
              <MessageSquare className="h-5 w-5 text-white" />
            </button>
          </div>
        )}
      </div>
      <div className="p-3">
        {currentRecap.series_name && (
          <h3 className="font-bold text-md text-gray-900 dark:text-white truncate group-hover:text-purple-700 dark:group-hover:text-purple-300 transition-colors" title={currentRecap.series_name}>
            {currentRecap.series_name}
          </h3>
        )}
        <p className={`text-md text-gray-900 dark:text-gray-300 truncate group-hover:text-purple-600 dark:group-hover:text-purple-200 transition-colors ${currentRecap.series_name ? 'mt-1' : ''}`} title={currentRecap.title}>
          {currentRecap.title}
        </p>
        <div className="flex items-center gap-1 mb-2 mt-2">
          <Link to={`/recaps/channel/${recap.channel_id}`} onClick={(e) => e.stopPropagation()} className="text-xs text-gray-500 hover:text-purple-400 transition-colors">
            {currentRecap.channel_name}
          </Link>
          {isAuthenticated && Boolean(user?.is_admin) && recap.channel_id && (
            <button
              onClick={handleDeactivateChannel}
              className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors"
              title={`Deactivate channel: ${currentRecap.channel_name}`}
            >
              <PowerOff className="w-3 h-3" />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between text-xs text-gray-400">
          <div className="flex items-center space-x-1">
            <StarRating rating={currentRating || 0} onRate={handleRate} ratingCount={ratingCount} readOnly={!isAuthenticated} />
          </div>
          <div className="flex items-center space-x-1">
            <Calendar className="h-4 w-4" />
            <span>{formatDateShort(currentRecap.published_at)}</span>
          </div>
        </div>
        {isAuthenticated && userRating != null && (
          <p className="text-xs text-purple-400 mt-1">Your rating: {userRating}</p>
        )}
      </div>

      <SeriesSuggestionModal
        isOpen={showSuggestionModal}
        onClose={() => setShowSuggestionModal(false)}
        recapId={recap.id}
        currentSeriesName={currentRecap.series_name}
        onSuggestionSubmitted={() => {
          // Optionally refresh the recap data or show a success message
          toast.success('Series name suggestion submitted!');
        }}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if recap data actually changed
  return (
    prevProps.recap.id === nextProps.recap.id &&
    prevProps.recap.thumbnail_url === nextProps.recap.thumbnail_url &&
    prevProps.recap.views === nextProps.recap.views &&
    prevProps.recap.like_count === nextProps.recap.like_count &&
    prevProps.recap.avg_rating === nextProps.recap.avg_rating &&
    prevProps.recap.rating_count === nextProps.recap.rating_count &&
    prevProps.recap.status === nextProps.recap.status &&
    prevProps.onClick === nextProps.onClick
  );
});